import { useEffect, useRef, useState } from 'react'
import { Button, Modal, Select, Textarea } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useBoard, useBoardList } from '@/pages/Boards/hooks'
import { useLinkOutgoingFax, useUpdateOutgoingFaxLink } from '@/hooks/useFaxTasks'

/** The link being edited: where it sits now, and what it currently says. */
export type EditingFaxLink = { itemId: string; boardId: string; note: string | null }

type Props = {
  opened: boolean
  onClose: () => void
  messageId: string
  /**
   * Snapshot fields stored with the link, so the record still reads correctly
   * once the fax is deleted from RingCentral. Unused when editing — an existing
   * link keeps the snapshot it was created with.
   */
  recipient: string
  sentAt: string
  /**
   * Present when editing an existing link rather than creating one. The same
   * form serves both: a care coordinator can re-point the link at a different
   * task, change the note, or both in one save.
   */
  editing?: EditingFaxLink
}

/**
 * Record a sent fax against an existing care task, or edit a link already made
 * (PD-1606). Board first, then task: Care Queue loads one board at a time, so
 * there is no cross-board task list to search, and the two-step picker matches
 * the fax panel's create form.
 *
 * In edit mode the pickers start on the link's current task, so changing them is
 * a move and leaving them alone is a note-only edit — one form, one Save.
 * Mount this with a `key` that changes per link so the fields re-initialise.
 */
export function LinkFaxToTaskModal({
  opened,
  onClose,
  messageId,
  recipient,
  sentAt,
  editing,
}: Props) {
  const { data: boardList } = useBoardList()
  const [boardId, setBoardId] = useState<string | null>(editing?.boardId ?? null)
  const [itemId, setItemId] = useState<string | null>(editing?.itemId ?? null)
  const [note, setNote] = useState(editing?.note ?? '')
  const { data: board } = useBoard(boardId ?? undefined)
  const link = useLinkOutgoingFax()
  const update = useUpdateOutgoingFaxLink()

  const statusColumnId = board?.board.columns.find((c) => c.type === 'status')?.id
  // A task counts as finished by EITHER a Done status or membership of the
  // Completed group — an item dragged straight into that group keeps whatever
  // status it had, and checking status alone would sort it in with open work.
  const completedGroupId = board?.board.groups.find((g) => g.label === 'Completed')?.id
  // Open work first: a finished task is rarely the one being linked, but it
  // stays selectable — and labelled — so a late record can still be attached to
  // work that is already closed.
  const taskOptions = (board?.items ?? [])
    .map((item) => ({
      value: item.id,
      label: item.name,
      done:
        (!!statusColumnId && item.values[statusColumnId] === 'Done') ||
        (!!completedGroupId && item.groupId === completedGroupId),
    }))
    .sort((a, b) => Number(a.done) - Number(b.done))
    .map(({ value, label, done }) => ({ value, label: done ? `${label} (Done)` : label }))

  // Re-initialise on every OPEN, not just on mount. The parent keys this modal by
  // the link's item id, which does not change when the same link's own data does
  // — so after saving an edit, reopening the form showed the pre-edit note and
  // saving again silently reverted the change. Keyed on the open transition
  // rather than on `editing` so a background refetch can never clobber what the
  // user is part-way through typing.
  const wasOpen = useRef(false)
  useEffect(() => {
    if (opened && !wasOpen.current) {
      setBoardId(editing?.boardId ?? null)
      setItemId(editing?.itemId ?? null)
      setNote(editing?.note ?? '')
    }
    wasOpen.current = opened
  }, [opened, editing])

  const close = () => {
    setBoardId(editing?.boardId ?? null)
    setItemId(editing?.itemId ?? null)
    setNote(editing?.note ?? '')
    onClose()
  }

  const submit = async () => {
    if (!itemId) return
    try {
      if (editing) {
        await update.mutateAsync({
          itemId: editing.itemId,
          messageId,
          // Equal to the current task server-side means "note only", so this is
          // safe to send unconditionally.
          targetItemId: itemId,
          // Always sent: an emptied field clears the note, which an omitted
          // field would not.
          note: note.trim(),
        })
      } else {
        await link.mutateAsync({
          itemId,
          messageId,
          recipient,
          sentAt,
          note: note.trim() || undefined,
        })
      }
    } catch {
      // The hooks' onError already showed the failure. Leave the modal open with
      // the user's choices intact so they can retry without re-picking.
      return
    }
    showNotification({
      color: 'green',
      title: editing ? 'Updated' : 'Linked',
      message: editing ? 'Fax link updated.' : 'Fax linked to the task.',
    })
    close()
  }

  return (
    <Modal opened={opened} onClose={close} title={editing ? 'Edit fax link' : 'Link fax to task'}>
      <Select
        label="Board"
        placeholder="Choose a board"
        data={(boardList?.boards ?? []).map((b) => ({ value: b.id, label: b.name }))}
        value={boardId}
        onChange={(value) => {
          setBoardId(value)
          setItemId(null)
        }}
        searchable
      />
      <Select
        className="mt-3"
        label="Task"
        placeholder={boardId ? 'Choose a task' : 'Choose a board first'}
        data={taskOptions}
        value={itemId}
        onChange={setItemId}
        disabled={!boardId}
        searchable
      />
      <Textarea
        className="mt-3"
        label="Note (optional)"
        placeholder="What was faxed?"
        autosize
        minRows={2}
        maxLength={500}
        value={note}
        onChange={(e) => setNote(e.currentTarget.value)}
      />
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="subtle" color="gray" onClick={close}>
          Cancel
        </Button>
        <Button
          disabled={!itemId}
          loading={link.isPending || update.isPending}
          onClick={() => void submit()}
        >
          {editing ? 'Save' : 'Link'}
        </Button>
      </div>
    </Modal>
  )
}
